import { extractInstruction } from '@atlaskit/pragmatic-drag-and-drop-hitbox/tree-item';
import { getReorderDestinationIndex } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index';

const INITIAL_POS = 16384;

export default function moveNode({ nodes, source, location }) {
  const sourceId = source.data.id;
  const target = location.current.dropTargets.find((t) => t.data.type === 'node')?.data;
  if (!target) return;

  const instruction = extractInstruction(target);
  if (!instruction) return;

  const targetId = target.id;
  let newParentId = null;
  let position = null;
  let insertBeforeId = null;
  let insertAfterId = null;

  switch (instruction.type) {
    case 'make-child': {
      if (sourceId === targetId) return;

      newParentId = targetId;
      const children = getChildren(nodes, newParentId).filter((n) => n.id !== sourceId);
      const last = children.at(-1);

      position = last ? last.position + INITIAL_POS : INITIAL_POS;
      insertAfterId = last?.id ?? null;
      break;
    }

    case 'reorder-above':
    case 'reorder-below': {
      if (sourceId === targetId) return;
      newParentId = target.parentId ?? null;

      const siblings = getChildren(nodes, newParentId).filter((n) => n.id !== sourceId);
      const sourceNode = findNode(nodes, sourceId);
      const fullList = [...siblings, sourceNode].sort((a, b) => a.position - b.position);

      const indexOfTarget = fullList.findIndex((n) => n.id === targetId);
      const startIndex = fullList.findIndex((n) => n.id === sourceId);

      const edge = instruction.type === 'reorder-above' ? 'top' : 'bottom';

      const destIndex = getReorderDestinationIndex({
        startIndex,
        indexOfTarget,
        closestEdgeOfTarget: edge,
        axis: 'vertical',
      });

      const reordered = fullList.filter((n) => n.id !== sourceId);
      const insertBefore = reordered[destIndex] ?? null;
      const insertAfter = reordered[destIndex - 1] ?? null;

      insertBeforeId = insertBefore?.id ?? null;
      insertAfterId = insertAfter?.id ?? null;

      position = computePosition(insertBefore, insertAfter);
      break;
    }

    case 'reparent': {
      const { desiredLevel, currentLevel } = instruction;

      let parentId = target.parentId ?? null;
      for (let i = 0; i < currentLevel - desiredLevel; i++) {
        const parentNode = findNode(nodes, parentId);
        parentId = parentNode?.parentId ?? null;
      }
      newParentId = parentId;

      const siblings = getChildren(nodes, newParentId).filter((n) => n.id !== sourceId);
      const targetNode = findNode(nodes, targetId);

      let candidateId = targetId;
      let candidate = targetNode;
      while (candidate && candidate.parentId !== newParentId) {
        candidateId = candidate.parentId;
        candidate = findNode(nodes, candidateId);
      }

      const insertIndex = siblings.findIndex((n) => n.id === candidateId) + 1;
      const insertBefore = siblings[insertIndex] ?? null;
      const insertAfter = siblings[insertIndex - 1] ?? null;

      insertBeforeId = insertBefore?.id ?? null;
      insertAfterId = insertAfter?.id ?? null;

      position = computePosition(insertBefore, insertAfter);
      break;
    }

    default:
      return;
  }

  return {
    id: sourceId,
    parentId: newParentId,
    position,
    insertBeforeId,
    insertAfterId,
  };
}

// Utilities

function computePosition(insertBefore, insertAfter) {
  if (insertBefore && insertAfter) {
    return Math.floor((insertBefore.position + insertAfter.position) / 2);
  } else if (insertBefore) {
    return Math.floor(insertBefore.position / 2);
  } else if (insertAfter) {
    return insertAfter.position + INITIAL_POS;
  } else {
    return INITIAL_POS;
  }
}

function findNode(nodes, id) {
  for (const node of nodes) {
    if (node.id === id) return node;
    const found = findNode(node.children || [], id);
    if (found) return found;
  }
  return null;
}

function getChildren(nodes, parentId) {
  if (parentId == null) return nodes.filter((n) => n.parentId == null);
  const parent = findNode(nodes, parentId);
  return parent?.children || [];
}
